/**
 * Procurement Trackers — list page (Iter 402.2 · Phase 2).
 *
 * Lists all procurement trackers with supplier/status filters. Click
 * through opens the detail page (qty ordered/delivered/outstanding,
 * per-line expected delivery dates).
 */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Boxes, Plus, Package, Loader2, Filter, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import ProjectSearchSelect from "../components/shared/ProjectSearchSelect";

const STATUS_LABELS = {
  draft: { l: "Draft", cls: "border-white/20 text-[#a0aabf]" },
  ordered: { l: "Ordered", cls: "border-cyan-500/40 text-cyan-300 bg-cyan-500/10" },
  partial: { l: "Partial", cls: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  received: { l: "Received", cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
  closed: { l: "Closed", cls: "border-white/10 text-[#647087]" },
  cancelled: { l: "Cancelled", cls: "border-rose-500/40 text-rose-300 bg-rose-500/10" },
};

export default function ProcurementTrackers() {
  // [Iter 420] Deep-link support: ?project_id=xxx&new=1 comes from the
  // project's Linked tab "+ New" button. When present, we auto-open
  // the supplier picker AND stamp project_id on the created tracker
  // so the newly-created tracker is instantly linked to the project.
  const [searchParams] = useSearchParams();
  const preselectProjectId = searchParams.get("project_id") || null;
  const autoOpen = searchParams.get("new") === "1";
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filters, setFilters] = useState({ supplier_id: "", status: "", project_id: "" });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [overdue, setOverdue] = useState([]);
  const [overdueOpen, setOverdueOpen] = useState(false);
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  // Iter 425.d — project filter list for the top-of-page filter bar.
  const [projects, setProjects] = useState([]);

  // Iter 425.d — Load projects once for the filter dropdown. Cheap
  // list (no line-item hydration) so we ask for a generous limit.
  useEffect(() => {
    api.get("/projects", { params: { limit: 500 } })
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setProjects([]));
  }, []);

  // Iter 404 · Phase 4A — fetch overdue items once on mount, then re-
  // fetch after any filter change (cheap query, tracker-status pre-
  // filtered on the server).
  useEffect(() => {
    api.get("/procurement/alerts/overdue")
      .then((r) => setOverdue(r.data?.items || []))
      .catch(() => setOverdue([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.status) params.status = filters.status;
      if (filters.project_id) params.project_id = filters.project_id;
      const [r1, r2] = await Promise.all([
        api.get("/procurement/trackers", { params }),
        suppliers.length ? Promise.resolve({ data: { items: suppliers } })
                         : api.get("/suppliers", { params: { limit: 500 } }),
      ]);
      setRows(r1.data?.items || []);
      const s = r2.data?.items || r2.data || [];
      if (!suppliers.length) setSuppliers(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load trackers.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.supplier_id, filters.status, filters.project_id]);

  const create = async (supplierIdOverride = null) => {
    const sid = supplierIdOverride || filters.supplier_id;
    if (!sid) {
      // No supplier selected → open the inline picker instead of a
      // dead-click. Same flow works from the empty state.
      setSupplierPickerOpen(true);
      return;
    }
    setCreating(true);
    try {
      const body = { supplier_id: sid };
      // Iter 420 — auto-link to the project if we arrived here from
      // a Project's "+ New" deep-link.
      if (preselectProjectId) body.project_id = preselectProjectId;
      const { data } = await api.post("/procurement/trackers", body);
      if (preselectProjectId) {
        toast.success("Tracker created and linked to the project.");
      }
      window.location.href = `/procurement/${data.id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed.");
    } finally { setCreating(false); }
  };

  // Iter 420 — auto-open supplier picker when arriving via ?new=1.
  useEffect(() => {
    if (autoOpen) setSupplierPickerOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="procurement-trackers-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-md bg-[#1FB6C1]/10 border border-[#1FB6C1]/30">
          <Boxes className="w-5 h-5 text-[#1FB6C1]" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">Procurement Tracker</h1>
          <div className="text-xs text-[#a0aabf]">Supplier POs · deliveries · GRVs</div>
        </div>
        <button onClick={() => create()} disabled={creating}
          className="hv-btn hv-btn-primary text-xs"
          data-testid="procurement-tracker-new">
          <Plus className="w-3 h-3" /> {creating ? "Creating…" : "New tracker"}
        </button>
      </div>

      {overdue.length > 0 && (
        <div className="hv-card p-3 mb-3 border-amber-500/40"
             data-testid="procurement-overdue-banner">
          <button onClick={() => setOverdueOpen((v) => !v)}
                  className="w-full flex items-center gap-2 text-left"
                  data-testid="procurement-overdue-toggle">
            <div className="p-1.5 rounded bg-amber-500/15 border border-amber-500/40">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-200">
                {overdue.length} overdue item{overdue.length === 1 ? "" : "s"}
              </div>
              <div className="text-[10px] text-[#a0aabf]">
                Expected on or before today, still outstanding · click to
                {overdueOpen ? " collapse" : " expand"}
              </div>
            </div>
            <span className="text-[10px] text-amber-300/80">
              {overdue[0]?.days_overdue}d worst
            </span>
          </button>
          {overdueOpen && (
            <div className="mt-2 space-y-1 max-h-64 overflow-y-auto"
                 data-testid="procurement-overdue-list">
              {overdue.map((a) => (
                <Link key={a.item_id} to={`/procurement/${a.tracker_id}`}
                      className="block p-2 rounded bg-black/20 hover:bg-black/40 border border-white/5 hover:border-amber-500/40 transition"
                      data-testid={`procurement-overdue-row-${a.item_id}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold truncate text-[#dde3ec]">
                        {a.item_name || a.item_sku || "—"}
                      </div>
                      <div className="text-[10px] text-[#a0aabf]">
                        {a.supplier_name} · {a.tracker_name} ·
                        {" "}outstanding {a.qty_outstanding}
                        {a.expected_delivery_date && ` · due ${a.expected_delivery_date}`}
                      </div>
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0">
                      {a.days_overdue}d late
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="hv-card p-3 mb-4 flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-[#647087]" />
        {/* Iter 411 (Dirk, 2026-02-28) — searchable supplier picker.
            The plain <select> had no search which was painful when
            scrolling through 100+ suppliers to find one. */}
        <SupplierCombobox
          suppliers={suppliers}
          value={filters.supplier_id}
          onChange={(sid) => setFilters((f) => ({ ...f, supplier_id: sid }))}
          testIdPrefix="procurement-filter-supplier"
        />
        <select value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="hv-input text-xs" data-testid="procurement-filter-status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (<option key={k} value={k}>{v.l}</option>))}
        </select>
        {/* Iter 425.d — Project filter. Uses backend's `project_id`
            param which already family-tree expands to include indirect
            links (via SO / JC / picking list) — see procurement.py
            list_trackers. Iter 425.j — searchable combobox. */}
        <div className="min-w-[220px]">
          <ProjectSearchSelect
            value={filters.project_id}
            projects={projects}
            onChange={(v) => setFilters((f) => ({ ...f, project_id: v }))}
            testid="procurement-filter-project"
            noneLabel="All projects"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-xs text-[#a0aabf] py-8">
          <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="hv-card p-6 text-center text-sm text-[#a0aabf]" data-testid="procurement-empty">
          <Package className="w-8 h-8 mx-auto mb-2 text-[#647087]" />
          <div className="mb-3">No procurement trackers yet.</div>
          <button onClick={() => create()}
                  className="hv-btn hv-btn-primary text-xs"
                  data-testid="procurement-empty-new-btn">
            <Plus className="w-3 h-3" /> Create your first tracker
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => {
            const st = STATUS_LABELS[t.status] || STATUS_LABELS.draft;
            const pct = t.items_total ? Math.round((t.items_delivered / t.items_total) * 100) : 0;
            return (
              <Link key={t.id} to={`/procurement/${t.id}`}
                className="hv-card p-3 flex items-center gap-3 hover:border-[#1FB6C1]/40 transition"
                data-testid={`procurement-tracker-row-${t.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{t.name || t.id}</div>
                  <div className="text-[10px] text-[#a0aabf]">
                    {t.supplier_name} · {t.items_delivered}/{t.items_total} items received
                    {t.default_expected_delivery_date && ` · ETA ${t.default_expected_delivery_date}`}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${st.cls}`}>
                  {st.l}
                </span>
                <span className="text-xs font-mono text-[#dde3ec] w-10 text-right">{pct}%</span>
              </Link>
            );
          })}
        </div>
      )}

      {supplierPickerOpen && (
        <SupplierPickerModal
          suppliers={suppliers}
          q={pickerQ}
          setQ={setPickerQ}
          onClose={() => setSupplierPickerOpen(false)}
          onPick={(sid) => {
            setSupplierPickerOpen(false);
            setPickerQ("");
            setFilters((f) => ({ ...f, supplier_id: sid }));
            create(sid);
          }}
        />
      )}
    </div>
  );
}

function SupplierPickerModal({ suppliers, q, setQ, onClose, onPick }) {
  const term = (q || "").trim().toLowerCase();
  const shown = term
    ? suppliers.filter((s) => (s.name || "").toLowerCase().includes(term))
    : suppliers.slice(0, 40);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 pt-16"
      onClick={onClose}
      data-testid="procurement-supplier-picker"
    >
      <div className="hv-card w-full max-w-md p-3 space-y-2"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <div className="text-sm font-bold flex-1">Pick a supplier</div>
          <button onClick={onClose} className="hv-btn hv-btn-ghost text-xs"
                  data-testid="procurement-supplier-picker-close">
            ×
          </button>
        </div>
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search suppliers…"
          className="hv-input text-xs w-full"
          data-testid="procurement-supplier-picker-input"
        />
        <div className="max-h-80 overflow-y-auto space-y-0.5">
          {shown.length === 0 ? (
            <div className="text-xs text-[#647087] p-2 text-center">
              No matches.
            </div>
          ) : shown.map((s) => (
            <button
              key={s.id}
              onClick={() => onPick(s.id)}
              className="w-full text-left p-2 rounded hover:bg-white/5 border border-transparent hover:border-[#1FB6C1]/30 text-xs"
              data-testid={`procurement-supplier-picker-opt-${s.id}`}
            >
              <div className="font-bold text-[#dde3ec] truncate">{s.name}</div>
              {(s.contact_name || s.contact_email) && (
                <div className="text-[10px] text-[#647087] truncate">
                  {s.contact_name}{s.contact_name && s.contact_email ? " · " : ""}
                  {s.contact_email}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


/**
 * SupplierCombobox — Iter 411.
 *
 * Type-to-filter combobox replacing the plain <select> on the
 * procurement filter bar. Handles 100+ suppliers without forcing
 * the operator to scroll through the whole list.
 */
function SupplierCombobox({ suppliers, value, onChange, testIdPrefix }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const ref = React.useRef(null);
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);
  const selected = suppliers.find((s) => s.id === value);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? suppliers.filter((s) => (s.name || "").toLowerCase().includes(q))
    : suppliers;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="hv-input text-xs flex items-center gap-1 min-w-[10rem] justify-between"
        data-testid={testIdPrefix}
      >
        <span className="truncate">{selected ? selected.name : "All suppliers"}</span>
        <span className="text-[#647087]">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-72 max-h-80 overflow-y-auto rounded border border-white/10 bg-[#0b1220] shadow-xl">
          <div className="p-1.5 border-b border-white/5 sticky top-0 bg-[#0b1220]">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search supplier…"
              className="w-full bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white focus:border-[#1FB6C1]/60 focus:outline-none"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>
          <div className="py-1">
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
              className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-white/5 ${!value ? "text-[#1FB6C1]" : "text-[#cbd2df]"}`}
              data-testid={`${testIdPrefix}-opt-all`}
            >
              All suppliers
            </button>
            {filtered.map((s) => (
              <button
                type="button"
                key={s.id}
                onClick={() => { onChange(s.id); setOpen(false); setQuery(""); }}
                className={`w-full text-left px-3 py-1.5 text-[11px] hover:bg-white/5 truncate ${value === s.id ? "text-[#1FB6C1]" : "text-[#cbd2df]"}`}
                data-testid={`${testIdPrefix}-opt-${s.id}`}
              >
                {s.name}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-[10px] text-[#647087] text-center">No matches.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
